import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { FeatureGroup, useMap } from "react-leaflet";
import { Path } from "leaflet";
import type { FeatureGroup as LeafletFeatureGroup, Layer, LayerEvent } from "leaflet";

import { stackOrder } from "./layerStackOrder";

type Entry = { group: LeafletFeatureGroup; drawOrder?: () => Layer[] };

type Stack = {
  register: (rank: number, entry: Entry) => void;
  unregister: (rank: number, group: LeafletFeatureGroup) => void;
  restack: () => void;
};

const LayerStackContext = createContext<Stack | null>(null);

function bringToFront(layers: Layer[]) {
  layers.forEach(layer => {
    if (layer instanceof Path) layer.bringToFront();
  });
}

// Note: a canvas draws, and hands the pointer to, whatever path was added last, and each layer's data
// arrives or is toggled back on whenever it likes. Every change re-applies LAYER_RANK from the bottom up.
export const LayerStack: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const entries = useRef(new Map<number, Entry>());
  const map = useMap();

  const stack = useMemo<Stack>(() => ({
    register: (rank, entry) => entries.current.set(rank, entry),
    unregister: (rank, group) => {
      if (entries.current.get(rank)?.group === group) entries.current.delete(rank);
    },
    restack: () => stackOrder(entries.current)
      .forEach(({ group, drawOrder }) => bringToFront(drawOrder ? drawOrder() : group.getLayers())),
  }), []);

  // Note: a path added after its group restacked lands on top regardless, and StrictMode re-adds every
  // newly mounted layer once the effects have run, which put the site extent over the whole map in
  // development. So any add restacks once more, before the browser paints.
  useEffect(() => {
    let pending = false;
    let detached = false;

    const onLayerAdd = ({ layer }: LayerEvent) => {
      if (!(layer instanceof Path) || pending) return;

      pending = true;
      queueMicrotask(() => {
        pending = false;
        if (!detached) stack.restack();
      });
    };

    map.on("layeradd", onLayerAdd);

    return () => {
      detached = true;
      map.off("layeradd", onLayerAdd);
    };
  }, [map, stack]);

  return <LayerStackContext.Provider value={stack}>{children}</LayerStackContext.Provider>;
};

type StackedGroupProps = {
  rank: number;
  /** Note: changes whenever the group's paths change, which is what re-applies the stack. */
  version?: unknown;
  /** Note: the group's own paths in drawing order, where that order matters within the group. */
  drawOrder?: () => Layer[];
  children: React.ReactNode;
};

export const StackedGroup: React.FC<StackedGroupProps> = ({ rank, version, drawOrder, children }) => {
  const stack = useContext(LayerStackContext);
  const groupRef = useRef<LeafletFeatureGroup>(null);

  useEffect(() => {
    const group = groupRef.current;
    if (!stack || !group) return;

    stack.register(rank, { group, drawOrder });
    stack.restack();

    return () => stack.unregister(rank, group);
  }, [stack, rank, version, drawOrder]);

  if (!stack) return <>{children}</>;

  return <FeatureGroup ref={groupRef}>{children}</FeatureGroup>;
};
